import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import {
    ChangePercentage,
    DefaultCellRightAlign,
    IndexCell,
    TrafficShareWithVisits,
} from "app/components/React/Table/cells";
import { PercentageCellRightAlign } from "app/components/React/Table/cells/PercentageCell";
import { SegmentOrDomainCellWithoutDomainSign } from "app/components/React/Table/cells/SegmentOrDomainCell";
import { DefaultEllipsisHeaderCell } from "app/components/React/Table/headerCells";
import type { IDefaultCellHeaderProps } from "app/components/React/Table/headerCells/DefaultCellHeader";
import { CPCCell } from "pro-features/components/Workspace/TableCells/CPCCell";
import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";

import { getCompareStripedCircleGradient } from "../utils/getCompareStripedCircle";

import type { HiddenColumns } from "./types";

const DEFAULT_SORT_FIELD = "TotalShare";
const DEFAULT_SORT_DIRECTION = "desc";

const StyledNoData = styled.div`
    text-align: right;
`;

const getPeriodOverPeriodHeaderComponent = (
    props: IDefaultCellHeaderProps,
    isPeriodOverPeriod: boolean,
    isPrevious: boolean,
    iconName?: IconName,
) => {
    if (!isPeriodOverPeriod) {
        return (
            <DefaultEllipsisHeaderCell
                {...props}
                iconName={iconName}
            />
        );
    }

    const bulletColor = isPrevious
        ? getCompareStripedCircleGradient(colorsPalettes.carbon[300])
        : colorsPalettes.carbon[300];

    return (
        <DefaultEllipsisHeaderCell
            {...props}
            bulletColor={bulletColor}
            iconName={iconName}
        />
    );
};

const getDomainColumns = (hasSegments: boolean) => {
    return {
        field: "domain",
        width: 185,
        sortable: true,
        showTotalCount: true,
        displayName: translate("market.research.players.table.columns.player"),
        tooltip: hasSegments
            ? "widget.table.tooltip.topsites.domain.or.segment"
            : "widget.table.tooltip.topsites.domain",
        isResizable: true,
        cellComponent: SegmentOrDomainCellWithoutDomainSign,
        fixed: true,
    };
};

export const getColumns = (
    sortedColumn = { field: DEFAULT_SORT_FIELD, sortDirection: DEFAULT_SORT_DIRECTION },
    hasSegments: boolean,
    hiddenColumns: HiddenColumns = new Set<string>(),
    isPeriodOverPeriod: boolean = false,
    isDaily: boolean,
    isCustomIndustry: boolean = false,
    isConversionRatesAvailable: boolean = false,
): Array<{ field: string; [key: string]: unknown }> => {
    const columns = [
        {
            field: null,
            fixed: true,
            cellComponent: IndexCell,
            sortable: false,
            width: 65,
            dashboardHide: "true",
            disableHeaderCellHover: true,
        },
        { ...getDomainColumns(hasSegments) },
        isPeriodOverPeriod && {
            field: "previousShare",
            displayName: translate("market.research.players.table.columns.monthly.visits"),
            tooltip: translate("market.research.players.table.columns.monthly.visits.tooltip.previous"),
            type: "string",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp={"previousTotalVisits"}
                />
            ),
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            showTotalCount: false,
            minWidth: 143,
            isResizable: false,
        },
        isPeriodOverPeriod && {
            field: "previousAdjacentPeriodChange",
            displayName: translate("market.research.players.table.columns.pop.change"),
            tooltip: translate("market.research.players.table.columns.pop.change.tooltip.previous"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: ChangePercentage,
            showTotalCount: false,
            width: 130,
            isResizable: true,
        },
        {
            field: "share",
            displayName: translate(
                isDaily
                    ? "market.research.players.table.columns.daily.visits"
                    : "market.research.players.table.columns.monthly.visits",
            ),
            tooltip: translate(
                isDaily
                    ? "market.research.players.table.columns.daily.visits.tooltip"
                    : "market.research.players.table.columns.monthly.visits.tooltip",
            ),
            type: "string",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp={"totalVisits"}
                />
            ),
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            showTotalCount: false,
            minWidth: 143,
            isResizable: false,
        },
        {
            field: "adjacentPeriodChange",
            displayName: translate("market.research.players.table.columns.pop.change"),
            tooltip: translate("market.research.players.table.columns.pop.change.tooltip"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: ChangePercentage,
            showTotalCount: false,
            width: 130,
            isResizable: true,
        },
        isConversionRatesAvailable &&
            isPeriodOverPeriod && {
                field: "previousConversionRate",
                displayName: translate("market.research.players.excel.columns.conversion.rate.prev"),
                tooltip: translate("market.research.players.excel.columns.conversion.rate.prev"),
                type: "double",
                format: "percentagesign",
                fixed: false,
                sortable: true,
                headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
                cellComponent: DefaultCellRightAlign,
                showTotalCount: false,
                width: 190,
                minWidth: 168,
                isResizable: true,
            },
        isConversionRatesAvailable && {
            field: "conversionRate",
            displayName: translate("market.research.players.excel.columns.conversion.rate"),
            tooltip: translate("market.research.players.excel.columns.conversion.rate"),
            type: "double",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 130,
            minWidth: 120,
            isResizable: true,
        },
        isConversionRatesAvailable &&
            isPeriodOverPeriod && {
                field: "previousConvertedVisits",
                displayName: translate("market.research.players.excel.columns.converted.visits.prev"),
                tooltip: translate("market.research.players.excel.columns.converted.visits.prev"),
                type: "double",
                format: "minVisitsAbbr",
                fixed: false,
                sortable: true,
                headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
                cellComponent: DefaultCellRightAlign,
                showTotalCount: false,
                width: 200,
                minWidth: 176,
                isResizable: true,
            },
        isConversionRatesAvailable && {
            field: "convertedVisits",
            displayName: translate("market.research.players.excel.columns.converted.visits"),
            tooltip: translate("market.research.players.excel.columns.converted.visits"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 140,
            minWidth: 130,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousDirectVisits",
            displayName: translate("market.research.players.table.columns.direct.traffic"),
            tooltip: translate("market.research.players.table.columns.direct.traffic.tooltip.previous"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 130,
            isResizable: true,
        },
        {
            field: "directVisits",
            displayName: translate("market.research.players.table.columns.direct.traffic"),
            tooltip: translate("market.research.players.table.columns.direct.traffic.tooltip"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 130,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousBrandedVolumeShare",
            displayName: translate("market.research.players.table.columns.branded.search.volume"),
            tooltip: translate("market.research.players.table.columns.branded.search.volume.tooltip.previous"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp={"previousBrandedVolume"}
                    value={props.row.previousBrandedVolumeShare}
                />
            ),
            showTotalCount: false,
            width: 190,
            isResizable: true,
        },
        {
            field: "brandedVolumeShare",
            displayName: translate("market.research.players.table.columns.branded.search.volume"),
            tooltip: translate("market.research.players.table.columns.branded.search.volume.tooltip"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp={"brandedVolume"}
                    value={props.row.brandedVolumeShare}
                />
            ),
            showTotalCount: false,
            width: 190,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousPpcSpend",
            displayName: translate("market.research.players.table.columns.ppc.spend"),
            tooltip: translate("market.research.players.table.columns.ppc.spend.tooltip.previous"),
            fixed: false,
            sortable: true,
            type: "double",
            format: "number",
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: CPCCell,
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        {
            field: "ppcSpend",
            displayName: translate("market.research.players.table.columns.ppc.spend"),
            tooltip: translate("market.research.players.table.columns.ppc.spend.tooltip"),
            fixed: false,
            sortable: true,
            type: "double",
            format: "number",
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: CPCCell,
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousAvgUniqueUsers",
            displayName: translate("market.research.players.table.columns.unique.visitors"),
            tooltip: translate("market.research.players.table.columns.unique.visitors.tooltip.previous"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 140,
            isResizable: true,
        },
        {
            field: "avgUniqueUsers",
            displayName: translate("market.research.players.table.columns.unique.visitors"),
            tooltip: translate("market.research.players.table.columns.unique.visitors.tooltip"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            width: 140,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousPeriodChange",
            displayName: translate("market.research.players.table.columns.period.change"),
            tooltip: translate("market.research.players.table.columns.period.change.tooltip.previous"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: ChangePercentage,
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        {
            field: "periodChange",
            displayName: translate("market.research.players.table.columns.period.change"),
            tooltip: translate("market.research.players.table.columns.period.change.tooltip"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: ChangePercentage,
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousExclusiveVisitors",
            displayName: translate("market.research.players.table.columns.exclusive.visitors"),
            tooltip: translate("market.research.players.table.columns.exclusive.visitors.tooltip.previous"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: (props) => {
                const { value } = props;
                if (!value) {
                    return <StyledNoData>{NOT_AVAILABLE}</StyledNoData>;
                }
                return <PercentageCellRightAlign {...props} />;
            },
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        {
            field: "exclusiveVisitors",
            displayName: translate("market.research.players.table.columns.exclusive.visitors"),
            tooltip: translate("market.research.players.table.columns.exclusive.visitors.tooltip"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: (props) => {
                const { value } = props;
                if (!value) {
                    return <StyledNoData>{NOT_AVAILABLE}</StyledNoData>;
                }
                return <PercentageCellRightAlign {...props} />;
            },
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        isPeriodOverPeriod && {
            field: "previousNewVsReturning",
            displayName: translate("market.research.players.table.columns.new.vs.returning"),
            tooltip: translate("market.research.players.table.columns.new.vs.returning.tooltip.previous"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, true),
            cellComponent: (props) => {
                const { value } = props;
                if (!value) {
                    return <StyledNoData>{NOT_AVAILABLE}</StyledNoData>;
                }
                return <PercentageCellRightAlign {...props} />;
            },
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
        {
            field: "newVsReturning",
            displayName: translate("market.research.players.table.columns.new.vs.returning"),
            tooltip: translate("market.research.players.table.columns.new.vs.returning.tooltip"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: (props) => getPeriodOverPeriodHeaderComponent(props, isPeriodOverPeriod, false),
            cellComponent: (props) => {
                const { value } = props;
                if (!value) {
                    return <StyledNoData>{NOT_AVAILABLE}</StyledNoData>;
                }
                return <PercentageCellRightAlign {...props} />;
            },
            showTotalCount: false,
            width: 120,
            isResizable: true,
        },
    ]
        .filter((column) => {
            return !!column;
        })
        .map((col) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                ...col,
                visible: !hiddenColumns.has(col.field),
                isSorted: isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : DEFAULT_SORT_DIRECTION,
            };
        });
    return columns;
};
